import React, { createContext, useContext } from 'react';
import { message } from '$/components/Message';
import { useMyIpLoginCreationAction } from '@/hooks/myIp/useMyIpLoginCreationAction';
import { useMyIpCreationCopy } from '$/pages/RoboneoMyIps/useMyIpCreationCopy';

interface HomeIpCreationEntryProps {
  className?: string;
  children?: React.ReactNode;
}

interface HomeIpCreationController {
  disabled: boolean;
  handleCreate: () => Promise<void>;
}

const HomeIpCreationContext = createContext<HomeIpCreationController | undefined>(undefined);

/** 首页复用登录续接动作，并保留原有创建失败提示。 */
function useHomeIpCreationController() {
  const { create, disabled } = useMyIpLoginCreationAction();
  /** 稳定 Provider 持有动作，按钮只负责处理可见的失败反馈。 */
  const handleCreate = async () => {
    try { await create(); } catch { message.error('RoboNeo_Web_chat_send_fail'); }
  };
  return { disabled, handleCreate };
}

/** 首页统一持有创建动作，避免登录分支切换卸载正在等待会话更新的控制器。 */
export function HomeIpCreationProvider({ children }: { children: React.ReactNode; }) {
  const controller = useHomeIpCreationController();
  return <HomeIpCreationContext.Provider value={controller}>{children}</HomeIpCreationContext.Provider>;
}

/** 统一按钮语义与禁用状态，宣传卡和列表内卡片只提供各自的视觉内容。 */
function CreationButton({ className, children, controller }: HomeIpCreationEntryProps & {
  controller: HomeIpCreationController;
}) {
  const { home } = useMyIpCreationCopy();
  return (
    <button
      type="button"
      className={className}
      disabled={controller.disabled}
      onClick={controller.handleCreate}
    >
      {children ?? home.createFallback}
    </button>
  );
}

/** 独立挂载入口时仍自行持有控制器，兼容既有宿主和入口调用。 */
function StandaloneCreationEntry({ className, children }: HomeIpCreationEntryProps) {
  const controller = useHomeIpCreationController();
  return <CreationButton className={className} controller={controller}>{children}</CreationButton>;
}

/** 有首页父层时共享登录续接，其他宿主沿用独立创建入口。 */
export default function HomeIpCreationEntry({ className, children }: HomeIpCreationEntryProps) {
  const controller = useContext(HomeIpCreationContext);
  return controller
    ? <CreationButton className={className} controller={controller}>{children}</CreationButton>
    : <StandaloneCreationEntry className={className}>{children}</StandaloneCreationEntry>;
}
